import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import styled from "styled-components";
import {
    Form,
    Input,
    Button,
    message,
    Spin,
    Switch,
    Modal,
    Table,
    Checkbox,
    Tag,
} from "antd";

const DashboardStyle = styled.div`
  margin-left: 280px;
  overflow-x: hidden;
`;

const BASE_URL = process.env.REACT_APP_BASE_URL;

const FundsPage = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [fundingLoading, setFundingLoading] = useState(false);
    const [accountBalance, setAccountBalance] = useState(null);
    const [currencyCode, setCurrencyCode] = useState("KES");
    const [fundingEnabled, setFundingEnabled] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [usersToFund, setUsersToFund] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const { token } = useAuth();

    // Initialize funding state from localStorage on component mount
    useEffect(() => {
        const savedState = localStorage.getItem('fundingState');
        if (savedState) {
            const { enabled, expiryTime } = JSON.parse(savedState);
            const now = Date.now();

            if (enabled && expiryTime > now) {
                setFundingEnabled(true);
                setTimeRemaining(Math.ceil((expiryTime - now) / 1000));
            } else {
                localStorage.removeItem('fundingState');
                setFundingEnabled(false);
                setTimeRemaining(0);
            }
        }
    }, []);

    // Timer effect
    useEffect(() => {
        let interval;

        if (fundingEnabled && timeRemaining > 0) {
            interval = setInterval(() => {
                setTimeRemaining((prev) => {
                    if (prev <= 1) {
                        setFundingEnabled(false);
                        localStorage.removeItem('fundingState');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [fundingEnabled, timeRemaining]);

    const fetchAccountBalance = async () => {
        try {
            const res = await axios.get(
                `${BASE_URL}/companies/manager/details/billing`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            setAccountBalance(res.data.account_balance ?? 0);
            setCurrencyCode(res.data.currency_code);
        } catch (error) {
            message.error("Failed to load account balance.");
        }
    };

    useEffect(() => {
        fetchAccountBalance();
    }, []);

    const handleSubmit = async ({ mpesa_code }) => {
        if (!mpesa_code)
            return message.error("Please enter the M-Pesa transaction code.");
        setLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/companies/verify/mpesa-code`,
                { transaction_code: mpesa_code },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.status === "success") {
                message.success("Transaction verified successfully");
                form.resetFields();
                fetchAccountBalance();
            } else {
                message.error(response.data.message || "Transaction failed.");
            }
        } catch (error) {
            message.error("Failed to process transaction.");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsersToFund = async () => {
        setPreviewLoading(true);
        try {
            const response = await axios.get(
                `${BASE_URL}/companies/preview-fundable-users`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (response.data.status === "success") {
                setUsersToFund(response.data.users);
                setSelectedUserIds(response.data.users.map(u => u.subscription_user_id));
                return response.data.users;
            } else {
                message.error("Failed to fetch users");
                return [];
            }
        } catch (error) {
            console.error("Error fetching users:", error);
            message.error("Failed to fetch users. Please try again.");
            return [];
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleFundingToggle = async (checked) => {
        if (checked) {
            setFundingLoading(true);
            try {
                const users = await fetchUsersToFund();

                if (users.length === 0) {
                    message.warning({
                        content: "No participants found. Please ensure: (1) Participants are marked as Active and (2) Active Participants have not been funded recently in the last 30 days.",
                        duration: 20,
                    });
                    setFundingLoading(false);
                    return;
                }

                setShowPreviewModal(true);
            } finally {
                setFundingLoading(false);
            }
        } else {
            setFundingEnabled(false);
            setTimeRemaining(0);
            localStorage.removeItem('fundingState');
        }
    };

    const handleConfirmFunding = async () => {
        if (selectedUserIds.length === 0) {
            message.warning("Please select at least one user to fund.");
            return;
        }

        setFundingLoading(true);
        try {
            const response = await axios.post(
                `${BASE_URL}/companies/fund-notified-users`,
                { user_ids: selectedUserIds },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    withCredentials: true,
                }
            );

            if (response.data.status === "success") {
                message.success(response.data.message);

                const expiryTime = Date.now() + (15 * 60 * 1000);
                setFundingEnabled(true);
                setTimeRemaining(15 * 60);

                localStorage.setItem('fundingState', JSON.stringify({
                    enabled: true,
                    expiryTime
                }));

                setShowPreviewModal(false);
                fetchAccountBalance();
            } else {
                message.error("Failed to fund users");
            }
        } catch (error) {
            console.error("Error funding users:", error);

            // Handle different error response formats
            let errorMessage = "Failed to fund users. Please try again.";

            if (error.response?.data) {
                const errorData = error.response.data;

                // Handle FastAPI validation errors
                if (errorData.detail) {
                    if (typeof errorData.detail === 'string') {
                        errorMessage = errorData.detail;
                    } else if (Array.isArray(errorData.detail)) {
                        // Handle validation error array
                        errorMessage = errorData.detail
                            .map(err => err.msg || JSON.stringify(err))
                            .join(', ');
                    }
                } else if (errorData.message) {
                    errorMessage = errorData.message;
                }
            }

            message.error(errorMessage);
        } finally {
            setFundingLoading(false);
        }
    };

    const handleRemoveUser = (userId) => {
        setSelectedUserIds(prev => prev.filter(id => id !== userId));
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedUserIds(usersToFund.map(u => u.subscription_user_id));
        } else {
            setSelectedUserIds([]);
        }
    };

    const columns = [
        {
            title: <Checkbox
                checked={selectedUserIds.length === usersToFund.length && usersToFund.length > 0}
                indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < usersToFund.length}
                onChange={handleSelectAll}
            />,
            key: 'checkbox',
            width: 50,
            render: (_, record) => (
                <Checkbox
                    checked={selectedUserIds.includes(record.subscription_user_id)}
                    onChange={(e) => {
                        if (e.target.checked) {
                            setSelectedUserIds(prev => [...prev, record.subscription_user_id]);
                        } else {
                            handleRemoveUser(record.subscription_user_id);
                        }
                    }}
                />
            ),
        },
        {
            title: 'Name',
            key: 'name',
            render: (_, record) => `${record.first_name} ${record.last_name}`,
        },
        {
            title: 'Email',
            dataIndex: 'user_email',
            key: 'email',
        },
        {
            title: 'Amount',
            key: 'amount',
            render: (_, record) => (
                <Tag color="blue">
                    {record.currency_code} {Number(record.amount).toLocaleString()}
                </Tag>
            ),
        },
    ];

    const selectedUsers = usersToFund.filter(u =>
        selectedUserIds.includes(u.subscription_user_id)
    );
    const totalAmount = selectedUsers.reduce((sum, user) => sum + user.amount, 0);

    return (
        <DashboardStyle>
            <div className="content-container">
                <br />
                <div className="col">
                    <h3>Funds Management</h3>
                    <br />
                </div>
                <main
                    className="main-content border-radius-lg card container-fluid"
                    style={{ padding: "23px" }}
                >
                    <div className="row mb-4">
                        <div className="col-12">
                            <div style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                flexWrap: "wrap",
                                gap: "10px"
                            }}>
                                <h6
                                    style={{
                                        fontWeight: "bold",
                                        color: "#5e95ff",
                                        marginBottom: "0",
                                        fontSize: "18px",
                                    }}
                                >
                                    Current Wallet Balance:{" "}
                                    <span style={{ color: "#5e95ff", fontSize: "18px" }}>
                                        {currencyCode} {Number(accountBalance).toLocaleString()}
                                    </span>
                                </h6>

                                <div style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    flexDirection: "row"
                                }}>
                                    <span style={{
                                        fontSize: "14px",
                                        color: "black",
                                        whiteSpace: "nowrap"
                                    }}>
                                        Fund Active Participants:
                                    </span>
                                    <Switch
                                        checked={fundingEnabled}
                                        onChange={handleFundingToggle}
                                        disabled={fundingLoading}
                                        style={{
                                            backgroundColor: fundingEnabled ? "#3c2f61" : undefined
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row mb-4">
                        <div className="col-12">
                            <h5>Add Funds via M-Pesa</h5>
                            <p style={{ color: "grey", marginBottom: "20px" }}>
                                Enter the M-Pesa transaction code received after payment to add funds to your wallet.
                            </p>

                            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                                <Form.Item
                                    name="mpesa_code"
                                    label="M-Pesa Transaction Code"
                                    rules={[
                                        { required: true, message: "Transaction code is required" },
                                        { len: 10, message: "Transaction code must be 10 characters" },
                                    ]}
                                >
                                    <Input placeholder="e.g., QNR1F9XY23" />
                                </Form.Item>
                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        style={{ backgroundColor: "#3c2f61", color: "white" }}
                                        disabled={loading}
                                    >
                                        {loading ? <Spin size="small" /> : "Verify Transaction"}
                                    </Button>
                                </Form.Item>
                            </Form>
                        </div>
                    </div>
                </main>
            </div>

            <Modal
                title="Review Users to Fund"
                open={showPreviewModal}
                onCancel={() => setShowPreviewModal(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setShowPreviewModal(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="confirm"
                        type="primary"
                        loading={fundingLoading}
                        onClick={handleConfirmFunding}
                        disabled={selectedUserIds.length === 0}
                        style={{ backgroundColor: "#3c2f61" }}
                    >
                        Confirm & Fund {selectedUserIds.length} User{selectedUserIds.length !== 1 ? 's' : ''}
                    </Button>,
                ]}
            >
                {previewLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
                            <p style={{ margin: 0, fontWeight: 'bold' }}>
                                Total Amount: {currencyCode} {totalAmount.toLocaleString()}
                            </p>
                            <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
                                {selectedUserIds.length} of {usersToFund.length} users selected
                            </p>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={usersToFund}
                            rowKey="subscription_user_id"
                            pagination={false}
                            scroll={{ y: 400 }}
                        />
                    </>
                )}
            </Modal>
        </DashboardStyle>
    );
};

export default FundsPage;